import SurveyDetailTable from "./SurveyDetailTable";
import DashboardSkeleton from "./DashboardSkeleton";
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { WEB_API_DASHBOARD } from "../../env";
import { StaticImage } from "../../utils/image";
import ApiLaw from "../../api/law";
import { WatDatePicker } from "thaidatepicker-react";
import moment from "moment";
import ApiMaster from "../../api/master";
import Select from 'react-select';

// Helper function to format numbers with commas
const formatNumber = (value) => {
    if (value === null || value === undefined) return "0";
    const num = String(value).replace(/,/g, '');
    if (isNaN(num)) return value;
    return Number(num).toLocaleString('en-US');
};

const CHART_COLORS = [
    "#5C6Bc0",
    "#80CBC4",
    "#90CAF9",
    "#4DD0E1",
    "#C8E6c9",
    "#f48fb1",
    "#f8bbd0",
    "#b39ddb",
    "#ce93d8",
    "#9fa8da",
];

const FilterSelect = ({ label, options, value, onChange, className = "" }) => {
    const normalizedOptions = options.map((option) => (
        typeof option === "string" ? { value: option, label: option } : option
    ));
    const optionValueMap = useMemo(
        () => new Map(normalizedOptions.map((option) => [String(option.value ?? ""), option.value])),
        [normalizedOptions]
    );

    return (
        <label className={`flex flex-col gap-1 text-xs text-slate-500 ${className}`}>
            {label}
            <select
                value={value ?? ""}
                onChange={(event) => onChange(optionValueMap.get(event.target.value))}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-sky-400 focus:outline-none"
            >
                {normalizedOptions.map((option) => (
                    <option key={String(option.value ?? "")} value={String(option.value ?? "")}>
                        {option.label}
                    </option>
                ))}
            </select>
        </label>
    );
};

const DateFilterInput = ({ label, value, onChange }) => (
    <label className="flex flex-col flex-1 gap-1 text-xs text-slate-500">
        {label}
        <WatDatePicker
            value={value}
            onChange={(christDate) => onChange(christDate ? moment(christDate) : null)}
            inputStyle={{
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: "8px 12px",
                fontSize: 14,
                width: "100%",
            }}
            placeholder="วว/ดด/ปป"
            dateFormat={"yyyy-MM-dd"}
            displayFormat={"DD/MM/YYYY"}
            clearable={false}
            disabled={false}
            readOnly={false}
            yearBoundary={99}
        />
    </label>
);

const AgencyDashboard = () => {
    const history = useHistory();

    const getDefaultFiscalRange = () => {
        const now = moment();
        const isFiscalYearStart = now.month() >= 9;
        const startYear = isFiscalYearStart ? now.year() : now.year() - 1;
        const endYear = isFiscalYearStart ? now.year() + 1 : now.year();

        return {
            start: moment({ year: startYear, month: 9, date: 1 }),
            end: moment({ year: endYear, month: 8, date: 30 }),
        };
    };

    const defaultRange = getDefaultFiscalRange();
    const [startDate, setStartDate] = useState(defaultRange.start);
    const [endDate, setEndDate] = useState(defaultRange.end);
    const [selectedLawType, setSelectedLawType] = useState(0);
    const [selectedAgency, setSelectedAgency] = useState(0);
    const [selectedMinistry, setSelectedMinistry] = useState(0);
    const [surveyDetailFilter, setSurveyDetailFilter] = useState(null);
    const [tableSort, setTableSort] = useState({ field: "name", direction: "asc" });

    const [agencyOption, setAgencyOption] = useState([
        { value: 0, label: "ทุกหน่วยงาน" },
    ]);
    const [ministryOption, setMinistryOption] = useState([
        { value: 0, label: "ทุกกระทรวง" },
    ]);

    const [lawHierarchyOption, setLawHierarchyOption] = useState([
        { value: 0, label: "เลือกตามศักดิ์กฎหมาย" },
    ]);

    // API data states
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const activeRequest = useRef(null);

    // Fetch dashboard data from API
    const fetchDashboardData = useCallback(async () => {
        activeRequest.current?.abort();
        const controller = new AbortController();
        activeRequest.current = controller;
        setLoading(true);
        setError(null);

        try {
            const payload = {
                start_date: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                end_date: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                lawType: selectedLawType ?? "",
                agency_id: selectedAgency ?? 0,
                ministry_id: selectedMinistry ?? 0,
            };

            const response = await fetch(`${WEB_API_DASHBOARD}/dga-user-service-survey/dashboard/agency-dashboard`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
                signal: controller.signal,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            if (controller.signal.aborted) return;
            setDashboardData(data);
        } catch (err) {
            if (controller.signal.aborted) return;
            console.error("Error fetching agency dashboard data:", err);
            setError(err.message);
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [startDate, endDate, selectedLawType, selectedAgency, selectedMinistry]);

    useEffect(() => { setSurveyDetailFilter(null); }, [startDate, endDate, selectedLawType, selectedAgency, selectedMinistry]);

    // Fetch data when filters change
    useEffect(() => {
        fetchDashboardData();
        return () => activeRequest.current?.abort();
    }, [fetchDashboardData]);

    useEffect(() => {
        getLawHierarchyOption();
        getFilterOption();
    }, []);

    const getLawHierarchyOption = async () => {
        const result = await ApiLaw.getMaster();
        if (result.status == 200) {
            const { data } = result;
            const hierarchy = Array.isArray(data?.hirachy) ? data.hirachy : [];
            setLawHierarchyOption([
                { value: 0, label: "เลือกตามศักดิ์กฎหมาย" },
                ...hierarchy,
            ]);
        }
    };

    // Trigger resize after data loaded to fix chart scale
    useEffect(() => {
        if (dashboardData) {
            const timer = setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [dashboardData]);

    const getFilterOption = async () => {
        const filter = await ApiMaster.homeFilter();
        if (filter.status == 200) {
            const { data } = filter.data;
            setAgencyOption([...agencyOption, ...data.agency]);
            setMinistryOption([...ministryOption, ...data.ministry]);
        }
    };

    // Helper function to get delta styling based on value
    const getDeltaStyle = (delta) => {
        if (!delta) return { text: "เมื่อเทียบปีที่ผ่านมา", class: "text-slate-600" };

        const deltaStr = String(delta).trim();

        if (deltaStr === "0" || deltaStr === "0%" || deltaStr === "+0" || deltaStr === "+0%" || deltaStr === "-0" || deltaStr === "-0%") {
            return { text: "ไม่เปลี่ยนแปลง", class: "text-slate-600" };
        }

        if (deltaStr.startsWith("+") || (!deltaStr.startsWith("-") && parseFloat(deltaStr) > 0)) {
            return { text: "เพิ่มขึ้นจากปีที่ผ่านมา", class: "text-emerald-600" };
        }

        if (deltaStr.startsWith("-")) {
            return { text: "ลดลงจากปีที่ผ่านมา", class: "text-red-600" };
        }

        return { text: "เมื่อเทียบปีที่ผ่านมา", class: "text-slate-600" };
    };

    // Use API data or defaults
    const agencyMonthlyStats = useMemo(() => {
        const raw = dashboardData?.monthlyStats;
        if (Array.isArray(raw) && raw.length > 0) {
            // Sort by year and month
            const sorted = [...raw].sort((a, b) => {
                const yearDiff = (a.year_active ?? 0) - (b.year_active ?? 0);
                if (yearDiff !== 0) return yearDiff;
                return (a.month_active ?? 0) - (b.month_active ?? 0);
            });
            return sorted.map((item) => {
                const rawValue = item.total_agencies ?? 0;
                const parsed = Number(String(rawValue).replace(/,/g, ""));
                return {
                    label: `${item.month_name_th || item.month_active} ${Number(item.year_active) + 543}`,
                    value: Number.isNaN(parsed) ? 0 : parsed,
                    year: item.year_active,
                    month: item.month_active,
                };
            });
        }
        return [];
    }, [dashboardData]);
    const groupedAgencyRows = useMemo(() => {
        const agencies = Array.isArray(dashboardData?.agencies)
            ? dashboardData.agencies
            : [];
        const groupedAgencies = new Map();

        agencies.forEach((agency) => {
            const name = String(agency?.name || "").trim();
            const key = agency?.state_agency_id != null
                ? `id:${agency.state_agency_id}`
                : `name:${name}`;
            const value = Number(String(agency?.value ?? 0).replace(/,/g, ""));
            const current = groupedAgencies.get(key);

            if (current) {
                current.value += Number.isNaN(value) ? 0 : value;
                return;
            }

            groupedAgencies.set(key, {
                ...agency,
                name,
                value: Number.isNaN(value) ? 0 : value,
            });
        });

        return Array.from(groupedAgencies.values());
    }, [dashboardData]);

    const knownAgencyCount = groupedAgencyRows.filter((agency) => agency.state_agency_id != null).length;
    const hasUnknownAgency = groupedAgencyRows.some((agency) => agency.state_agency_id == null);

    const currentAgencies = useMemo(() => {
        return [...groupedAgencyRows].sort((a, b) => {
            let comparison;

            if (tableSort.field === "value") {
                comparison = a.value - b.value;
                if (comparison === 0) {
                    comparison = a.name.localeCompare(b.name, "th", { sensitivity: "base" });
                }
            } else {
                comparison = a.name.localeCompare(b.name, "th", { sensitivity: "base" });
            }

            return tableSort.direction === "asc" ? comparison : -comparison;
        });
    }, [groupedAgencyRows, tableSort]);

    const handleTableSort = (field) => {
        setTableSort((current) => ({
            field,
            direction: current.field === field && current.direction === "asc" ? "desc" : "asc",
        }));
    };

    const rangeLabel = useMemo(() => {
        if (!startDate || !endDate) {
            return "ทุกช่วงเวลา";
        }
        return `${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")}`;
    }, [startDate, endDate]);

    const csvEscape = (value) => {
        if (value === null || value === undefined) return "";
        const text = String(value);
        if (/[",\n]/.test(text)) {
            return `"${text.replace(/"/g, '""')}"`;
        }
        return text;
    };

    const handleDownloadExcel = () => {
        const rows = [
            ["ลำดับ", "หน่วยงาน", "จำนวนการรับฟัง"],
            ...currentAgencies.map((agency, idx) => [
                idx + 1,
                agency?.name || "",
                agency?.value ?? 0,
            ]),
        ];
        const csvContent = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
        const blob = new Blob(["\ufeff", csvContent], {
            type: "text/csv;charset=utf-8;",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `agency-dashboard-${moment().format(
            "YYYYMMDD"
        )}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };


    const monthChartOptions = useMemo(
        () => ({
            chart: {
                type: "bar",
                toolbar: { show: false },
                fontFamily: "Prompt, 'Noto Sans Thai', sans-serif",
                animations: {
                    enabled: false,
                },
                parentHeightOffset: 0,
            },
            plotOptions: {
                bar: {
                    distributed: true,
                    horizontal: false,
                    columnWidth: "40%",
                    borderRadius: 6,
                },
            },
            colors: CHART_COLORS,
            dataLabels: { enabled: false },
            grid: {
                borderColor: "#e2e8f0",
                strokeDashArray: 4,
                padding: {
                    left: 0,
                    right: 0,
                },
            },
            xaxis: {
                categories: agencyMonthlyStats.map((item) => item.label),
                labels: {
                    style: { colors: "#94a3b8", fontSize: "10px" },
                },
                axisTicks: { show: false },
                axisBorder: { show: false },
            },
            yaxis: {
                labels: {
                    formatter: (value) => formatNumber(value),
                    style: { colors: "#94a3b8", fontSize: "11px" },
                },
            },
            tooltip: {
                theme: "light",
                y: {
                    formatter: (value) => `${formatNumber(value)} หน่วยงาน`,
                },
            },
        }),
        [agencyMonthlyStats]
    );

    const monthChartSeries = useMemo(
        () => [
            {
                name: "การรับฟัง",
                data: agencyMonthlyStats.map((item) => item.value),
            },
        ],
        [agencyMonthlyStats]
    );

    const getAgencyInitials = (name = "") => {
        const parts = name.split(" ").filter(Boolean);
        return parts
            .slice(0, 2)
            .map((part) => part.charAt(0))
            .join("")
            .toUpperCase();
    };

    // Loading state
    if (loading) {
        return <DashboardSkeleton variant="agency" />;
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen bg-[#f3f6fb] pb-16 pt-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <div className="text-red-500 mb-4">
                                <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <p className="text-sm text-slate-500">เกิดข้อผิดพลาด: {error}</p>
                            <button
                                onClick={fetchDashboardData}
                                className="mt-4 px-4 py-2 bg-sky-500 text-white rounded-lg text-sm hover:bg-sky-600"
                            >
                                ลองใหม่อีกครั้ง
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f3f6fb] pb-16 pt-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-6">
                <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm space-y-1">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex items-center gap-x-2">
                            <button
                                onClick={() => history.go(-1)}
                                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-3 py-1 text-xs font-medium text-sky-600 hover:bg-slate-50"
                            >
                                <svg viewBox="0 0 24 24" className="h-4 w-4">
                                    <path
                                        d="M15 18l-6-6 6-6"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                ย้อนกลับ
                            </button>
                            <p className="text-sm text-primary font-bold">สถิติการใช้งานระบบกลางทางกฎหมาย (Law Portal): หน่วยงานที่เปิดรับฟังความคิดเห็น</p>
                            {loading && (
                                <span className="ml-2 text-xs text-slate-400">กำลังโหลด...</span>
                            )}
                        </div>
                    </div>
                    <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-end">
                        <div className="w-full gap-3 mt-3 flex">
                            <DateFilterInput

                                label="วันที่เริ่มต้น"
                                value={startDate}
                                onChange={setStartDate}
                            />
                            <DateFilterInput
                                label="วันที่สิ้นสุด"
                                value={endDate}
                                onChange={setEndDate}
                            />
                            <label className={`flex flex-col gap-1 text-xs text-slate-500`}>
                                {"ศักดิ์กฏหมาย"}
                                <Select
                                    className="basic-single w-[200px]"
                                    classNamePrefix="select"
                                    defaultValue={lawHierarchyOption[0]}
                                    options={lawHierarchyOption}
                                    value={lawHierarchyOption.find((x) => x.value == selectedLawType)}
                                    onChange={(item) => {
                                        setSelectedLawType(item.value);
                                    }}
                                />
                            </label>

                            <label className={`flex flex-col gap-1 text-xs text-slate-500`}>
                                {"กระทรวง"}
                                <Select
                                    // className="basic-single"
                                    className="basic-single w-[200px]"
                                    classNamePrefix="select"
                                    defaultValue={ministryOption[0]}
                                    value={ministryOption.find((x) => x.value == selectedMinistry)}
                                    options={ministryOption}
                                    onChange={(item) => {
                                        setSelectedMinistry(item.value);
                                        setSelectedAgency(0);
                                    }}
                                />
                            </label>
                            <label className={`flex flex-col gap-1 text-xs text-slate-500`}>
                                {"หน่วยงาน"}
                                <Select
                                    className="basic-single w-[200px]"
                                    classNamePrefix="select"
                                    defaultValue={agencyOption[0]}
                                    options={
                                        selectedMinistry > 0
                                            ? agencyOption.filter((x) => x.ministry_id == selectedMinistry)
                                            : agencyOption
                                    }
                                    value={agencyOption.find((x) => x.value == selectedAgency)}
                                    onChange={(item) => {
                                        setSelectedAgency(item.value);
                                    }}
                                />
                            </label>
                            <button
                                type="button"
                                onClick={handleDownloadExcel}
                                disabled={currentAgencies.length === 0}
                                className={`mt-5 mb-1 whitespace-nowrap inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold ${currentAgencies.length === 0
                                    ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                                    : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                    }`}
                            >
                                ดาวน์โหลด Excel
                            </button>
                        </div>
                    </div>
                </section>

                <section>

                    <div className="mx-auto w-full min-w-0 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">

                        <div className="flex items-start justify-between">
                            <div>
                                <div className="flex flex-col gap-y-2">
                                    <p className="text-xs font-semibold tracking-wide font-bold">
                                        ช่วงวันที่ {rangeLabel} : หน่วยงานที่เปิดรับฟังความเห็นรายเดือน
                                    </p>
                                </div>
                            </div>
                        </div>
                        <ReactApexChart
                            options={monthChartOptions}
                            series={monthChartSeries}
                            type="bar"
                            width="100%"
                            height={320}
                        />
                    </div>
                </section>
                <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-x-10">
                            <div className="flex items-center gap-x-3">
                                <span>ผลลัพธ์การกรองข้อมูล</span>
                                <img className="h-6 w-6" src={StaticImage("/images/icon_v3/blue/calendar-lines-duotone.svg")} />
                                <p className="text-sm font-semibold text-slate-900">
                                    {rangeLabel}
                                </p>
                            </div>
                            <div className="flex items-center gap-x-3">
                                <img className="h-6 w-6" src={StaticImage("/images/icon_v3/blue/building-columns-duotone.svg")} />
                                <p className="text-xs text-slate-700 font-bold">จำนวนหน่วยงานไม่ซ้ำ <span className="text-xl text-bold">{formatNumber(knownAgencyCount)}</span> หน่วยงาน</p>
                            </div>
                        </div>

                    </div>
                    {hasUnknownAgency && (
                        <p className="mt-3 text-xs text-slate-500">รายการที่ไม่ทราบชื่อหน่วยงานยังแสดงในตาราง แต่ไม่นับรวมในจำนวนหน่วยงาน</p>
                    )}
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left text-xs uppercase tracking-wide text-slate-700">
                                <tr>
                                    <th className="py-2 text-center">ลำดับ</th>
                                    <th className="py-2">
                                        <button
                                            type="button"
                                            onClick={() => handleTableSort("name")}
                                            className="inline-flex items-center gap-2 hover:text-sky-600"
                                            aria-label={`เรียงชื่อหน่วยงาน${tableSort.field === "name" && tableSort.direction === "asc" ? " ฮ ถึง ก" : " ก ถึง ฮ"}`}
                                        >
                                            หน่วยงาน
                                            <span className="flex flex-col text-[9px] leading-[7px]" aria-hidden="true">
                                                <span className={tableSort.field === "name" && tableSort.direction === "asc" ? "text-sky-600" : "text-slate-300"}>▲</span>
                                                <span className={tableSort.field === "name" && tableSort.direction === "desc" ? "text-sky-600" : "text-slate-300"}>▼</span>
                                            </span>
                                        </button>
                                    </th>
                                    <th className="py-2 text-center">
                                        <button
                                            type="button"
                                            onClick={() => handleTableSort("value")}
                                            className="inline-flex items-center gap-2 hover:text-sky-600"
                                            aria-label={`เรียงจำนวนการรับฟัง${tableSort.field === "value" && tableSort.direction === "asc" ? " มากไปน้อย" : " น้อยไปมาก"}`}
                                        >
                                            จำนวนการรับฟัง
                                            <span className="flex flex-col text-[9px] leading-[7px]" aria-hidden="true">
                                                <span className={tableSort.field === "value" && tableSort.direction === "asc" ? "text-sky-600" : "text-slate-300"}>▲</span>
                                                <span className={tableSort.field === "value" && tableSort.direction === "desc" ? "text-sky-600" : "text-slate-300"}>▼</span>
                                            </span>
                                        </button>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentAgencies.map((agency, idx) => (
                                    <tr
                                        key={agency.name}
                                        className="border-t border-slate-100 text-slate-700 hover:bg-slate-50"
                                    >
                                        <td className="py-3 text-center">
                                            <span className="text-sm text-slate-500">{formatNumber(idx + 1)}</span>
                                        </td>
                                        <td className="py-3">
                                            <div className="flex items-center gap-3">
                                                {/* <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-600">
                                                    {getAgencyInitials(agency.name)}
                                                </div> */}
                                                <span className="text-sm text-slate-800">{agency.name}</span>
                                            </div>
                                        </td>
                                        <td className="py-3 text-center text-sm font-semibold">
                                            <button type="button" className="text-sky-600 hover:underline"
                                                aria-label={`ดูการรับฟังของ${agency.name}`}
                                                aria-pressed={surveyDetailFilter?.agency_id === agency.state_agency_id}
                                                onClick={() => setSurveyDetailFilter({
                                                    start_date: moment(startDate).format("YYYY-MM-DD"),
                                                    end_date: moment(endDate).format("YYYY-MM-DD"),
                                                    lawType: selectedLawType || 0, agency_id: agency.state_agency_id ?? null,
                                                    ministry_id: selectedMinistry || 0, range_label: rangeLabel,
                                                    agency_name: agency.name,
                                                })}>{formatNumber(agency.value)}</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
                {surveyDetailFilter && <SurveyDetailTable filter={surveyDetailFilter} onClose={() => setSurveyDetailFilter(null)} />}
            </div>
        </div>
    );
};

export default AgencyDashboard;
